import axios from 'axios';

// Use same-origin API calls when running on a custom domain (e.g., cms.careo.co)
// Emergent sets REACT_APP_BACKEND_URL to emergent.host during build
// On custom domains, the backend is proxied at same origin via /api
const configuredUrl = process.env.REACT_APP_BACKEND_URL || '';
const isCustomDomain = typeof window !== 'undefined'
  && configuredUrl
  && window.location.origin !== configuredUrl
  && !window.location.hostname.includes('emergent')
  && !window.location.hostname.includes('localhost')
  && !window.location.hostname.includes('127.0.0.1');
const API_URL = isCustomDomain ? '' : configuredUrl;

// Create axios instance
const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
});

// Add auth header to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (userData) => api.post('/auth/register', userData),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  changePassword: (data) => api.post('/auth/change-password', data),
  forceChangePassword: (newPassword, token) => api.post('/auth/force-change-password', { new_password: newPassword }, { headers: { Authorization: `Bearer ${token}` } }),
  webauthnRegisterOptions: (userId) => api.post('/auth/webauthn/register-options', { user_id: userId }),
  webauthnRegisterVerify: (userId, credential, deviceName) => api.post('/auth/webauthn/register-verify', { user_id: userId, credential, device_name: deviceName }),
  webauthnAuthOptions: (userId) => api.post('/auth/webauthn/auth-options', { user_id: userId }),
  webauthnAuthVerify: (userId, credential) => api.post('/auth/webauthn/auth-verify', { user_id: userId, credential }),
  toggle2fa: (data) => api.put('/auth/toggle-2fa', data),
  get2faStatus: () => api.get('/auth/2fa-status'),
};

// Tenants API (Enhanced Hospital Management)
export const tenantsApi = {
  list: () => api.get('/tenants'),
  get: (id) => api.get(`/tenants/${id}`),
  create: (data) => api.post('/tenants', data),
  update: (id, data) => api.put(`/tenants/${id}`, data),
  toggle: (id) => api.patch(`/tenants/${id}/toggle`),
  uploadLogo: (id, formData) => api.post(`/tenants/${id}/upload-logo`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getUsers: (id) => api.get(`/tenants/${id}/users`),
  updateOnboarding: (id, status) => api.patch(`/tenants/${id}/onboarding?status=${status}`),
  resetAdminPassword: (id) => api.post(`/tenants/${id}/reset-password`),
  updateAdminEmail: (id, email) => api.put(`/tenants/${id}/admin-email`, { email }),
};

// Super Admin Dashboard
export const adminApi = {
  dashboard: () => api.get('/admin/dashboard'),
  renewals: () => api.get('/admin/renewals'),
  renewalReport: () => api.get('/admin/renewals/report'),
  sendRenewalAlert: (tenantId) => api.post(`/admin/renewals/send-alert/${tenantId}`),
  sendBulkAlerts: () => api.post('/admin/renewals/send-bulk-alerts'),
};

// Branches API (Enhanced)
export const branchesApi = {
  list: () => api.get('/branches'),
  create: (data) => api.post('/branches', data),
  update: (id, data) => api.put(`/branches/${id}`, data),
  toggle: (id) => api.patch(`/branches/${id}/toggle`),
  getUsers: (branchId) => api.get(`/branches/${branchId}/users`),
  getUnassigned: () => api.get('/branches/unassigned-users'),
  assignUser: (branchId, userId) => api.post(`/branches/${branchId}/assign-user`, { user_id: userId }),
  unassignUser: (branchId, userId) => api.post(`/branches/${branchId}/unassign-user`, { user_id: userId }),
};

// Numbering Configuration API
export const numberingApi = {
  list: () => api.get('/numbering-configs'),
  listForTenant: (tenantId) => api.get(`/numbering-configs/${tenantId}`),
  update: (id, data) => api.put(`/numbering-configs/${id}`, data),
  create: (data) => api.post('/numbering-configs', data),
  delete: (id) => api.delete(`/numbering-configs/${id}`),
};

// Payers API
export const payersApi = {
  list: () => api.get('/payers'),
  listActive: () => api.get('/payers/active'),
  create: (data) => api.post('/payers', data),
  update: (id, data) => api.put(`/payers/${id}`, data),
  toggle: (id) => api.patch(`/payers/${id}/toggle`),
  delete: (id) => api.delete(`/payers/${id}`),
  seedIndia: () => api.post('/payers/seed-india'),
  contactOverride: (id, data) => api.put(`/payers/${id}/contact-override`, data),
};

// Patients API
export const patientsApi = {
  list: (search) => api.get('/patients', { params: { search } }),
  get: (id) => api.get(`/patients/${id}`),
  create: (data) => api.post('/patients', data),
  update: (id, data) => api.put(`/patients/${id}`, data),
  importCsv: (formData) => api.post('/patients/import-csv', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  fetchHis: () => api.post('/patients/fetch-his'),
  getHisSettings: () => api.get('/hospital/settings/his'),
  updateHisSettings: (data) => api.put('/hospital/settings/his', data),
  generateHisKey: () => api.post('/hospital/settings/his/generate-key'),
  toggleHisPush: (data) => api.put('/hospital/settings/his/toggle-push', data),
};

// Claims API
export const claimsApi = {
  list: (params) => api.get('/claims', { params }),
  get: (id) => api.get(`/claims/${id}`),
  create: (data) => api.post('/claims', data),
  update: (id, data) => api.put(`/claims/${id}`, data),
  submit: (id) => api.post(`/claims/${id}/submit`),
  allowedStatuses: (id) => api.get(`/claims/${id}/allowed-statuses`),
};

// Claim Emails API
export const claimEmailsApi = {
  compose: (claimId, templateId) => api.get(`/claim-emails/compose/${claimId}${templateId ? `?template=${templateId}` : ''}`),
  send: (data) => api.post('/claim-emails/send', data),
  history: (claimId) => api.get(`/claim-emails/history/${claimId}`),
  templates: () => api.get('/claim-emails/templates'),
  updateTemplate: (id, data) => api.put(`/claim-emails/templates/${id}`, data),
  stats: () => api.get('/claim-emails/stats'),
  updatePayerEmails: (payerId, data) => api.put(`/payers/${payerId}/emails`, data),
  imapConfig: () => api.get('/claim-emails/imap-config'),
  updateImapConfig: (data) => api.put('/claim-emails/imap-config', data),
  testImap: () => api.post('/claim-emails/test-imap'),
  fetchIncoming: () => api.post('/claim-emails/fetch-incoming'),
  dashboard: () => api.get('/claim-emails/dashboard'),
  unreadCount: () => api.get('/claim-emails/unread-count'),
  incoming: (params) => api.get('/claim-emails/incoming', { params }),
  markRead: (id) => api.put(`/claim-emails/${id}/read`),
  linkToClaim: (id, claimId) => api.put(`/claim-emails/${id}/link`, { claim_id: claimId }),
};

// Documents API
export const documentsApi = {
  upload: (claimId, documentType, file, notes) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/documents/upload?claim_id=${claimId}&document_type=${documentType}${notes ? `&notes=${encodeURIComponent(notes)}` : ''}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  list: (claimId) => api.get(`/claims/${claimId}/documents`),
  download: (documentId) => api.get(`/documents/${documentId}/download`, { responseType: 'blob' }),
  delete: (documentId) => api.delete(`/documents/${documentId}`),
  verify: (documentId, verified = true) => api.put(`/documents/${documentId}/verify?verified=${verified}`),
  update: (documentId, data) => api.put(`/documents/${documentId}/update`, data),
};

// Document Checklist API
export const checklistApi = {
  create: (claimId) => api.post(`/claims/${claimId}/checklist`),
  list: (claimId) => api.get(`/claims/${claimId}/checklist`),
  update: (claimId, itemId, status, notes) => api.put(`/claims/${claimId}/checklist/${itemId}?status=${status}${notes ? `&notes=${encodeURIComponent(notes)}` : ''}`),
};

// Pre-Authorization API
export const preauthApi = {
  list: (params) => api.get('/preauths', { params }),
  get: (id) => api.get(`/preauths/${id}`),
  create: (data) => api.post('/preauths', data),
  update: (id, data) => api.put(`/preauths/${id}`, data),
};

// Denials API
export const denialsApi = {
  list: (params) => api.get('/denials', { params }),
  get: (id) => api.get(`/denials/${id}`),
  create: (data) => api.post('/denials', data),
  update: (id, data) => api.put(`/denials/${id}`, data),
  stats: () => api.get('/dashboard/denial-stats'),
};

// AR Follow-up API
export const arFollowupApi = {
  list: (params) => api.get('/ar-followups', { params }),
  create: (data) => api.post('/ar-followups', data),
  getForClaim: (claimId) => api.get(`/claims/${claimId}/ar-followups`),
  update: (id, data) => api.put(`/ar-followups/${id}`, data),
  delete: (id) => api.delete(`/ar-followups/${id}`),
  aging: () => api.get('/dashboard/ar-aging'),
};

// Display Board API
export const displayBoardApi = {
  get: (params) => api.get('/display-board', { params }),
  info: (params) => api.get('/display-board/info', { params }),
};

// Dashboard API
export const dashboardApi = {
  stats: (params) => api.get('/dashboard/stats', { params }),
  statusCounts: (params) => api.get('/dashboard/status-counts', { params }),
  payerStats: () => api.get('/dashboard/payer-stats'),
  recentClaims: (limit = 10) => api.get('/dashboard/recent-claims', { params: { limit } }),
  claimTrends: (days = 30) => api.get('/dashboard/claim-trends', { params: { days } }),
  denialStats: () => api.get('/dashboard/denial-stats'),
  arAging: () => api.get('/dashboard/ar-aging'),
};

// Executive Dashboard API
export const executiveApi = {
  dashboard: (period = 'monthly', dateFrom, dateTo) => {
    const params = { period };
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    return api.get('/executive/dashboard', { params });
  },
};

// HIPAA Security API
export const securityApi = {
  dashboard: () => api.get('/security/dashboard'),
  activeSessions: () => api.get('/security/active-sessions'),
  passwordPolicy: () => api.get('/security/password-policy'),
  exportPermission: () => api.get('/security/export-permission'),
  knownDevices: () => api.get('/security/known-devices'),
};

// HIPAA Policy Documentation API
export const policiesApi = {
  list: () => api.get('/policies'),
  create: (data) => api.post('/policies', data),
  update: (id, data) => api.put(`/policies/${id}`, data),
  remove: (id) => api.delete(`/policies/${id}`),
  seedDefaults: () => api.post('/policies/seed-defaults'),
};

// ABDM Integration API
export const abdmApi = {
  getSettings: () => api.get('/abdm/settings'),
  updateSettings: (data) => api.put('/abdm/settings', data),
  getNhcxSettings: () => api.get('/nhcx/settings'),
  updateNhcxSettings: (data) => api.put('/nhcx/settings', data),
  linkAbha: (data) => api.post('/abdm/link-abha', data),
  verifyAbha: (data) => api.post('/abdm/verify-abha', data),
  unlinkAbha: (patientId) => api.delete(`/abdm/unlink-abha/${patientId}`),
  getPatientAbha: (patientId) => api.get(`/abdm/patient/${patientId}`),
  dashboard: () => api.get('/abdm/dashboard'),
  // Super Admin
  getHospitalsStatus: () => api.get('/admin/abdm/hospitals'),
  toggleHospital: (tenantId, data) => api.post(`/admin/abdm/toggle/${tenantId}`, data),
};

// Tickets API
export const ticketsApi = {
  list: (params) => api.get('/tickets', { params }),
  get: (id) => api.get(`/tickets/${id}`),
  create: (data) => api.post('/tickets', data),
  update: (id, data) => api.put(`/tickets/${id}`, data),
  addComment: (id, data) => api.post(`/tickets/${id}/comments`, data),
  uploadAttachment: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/tickets/${id}/attachments`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  dashboard: () => api.get('/tickets/dashboard'),
  categories: () => api.get('/tickets/categories'),
  slaConfig: () => api.get('/tickets/sla-config'),
  updateSlaConfig: (data) => api.put('/tickets/sla-config', data),
  assignableUsers: () => api.get('/tickets/assignable-users'),
  config: () => api.get('/tickets/config'),
  updateConfig: (data) => api.put('/tickets/config', data),
  runAutomation: () => api.post('/tickets/run-automation'),
  adminOverview: () => api.get('/admin/tickets/overview'),
  adminHospitalTickets: (hospitalId, params) => api.get(`/admin/tickets/hospital/${hospitalId}`, { params }),
  adminGetTicket: (id) => api.get(`/admin/tickets/detail/${id}`),
  adminUpdateTicket: (id, data) => api.put(`/admin/tickets/detail/${id}`, data),
  adminAddComment: (id, data) => api.post(`/admin/tickets/detail/${id}/comments`, data),
  adminRaiseTicket: (data) => api.post('/admin/tickets/raise', data),
  adminGetHospitalUsers: (tenantId) => api.get(`/admin/tickets/hospital-users/${tenantId}`),
  reports: (params) => api.get('/tickets/reports', { params }),
};

// Analytics API
export const analyticsApi = {
  getData: () => api.get('/analytics/data'),
  getInsights: (focus) => api.get('/analytics/ai-insights', { params: { focus } }),
  getHistory: (limit) => api.get('/analytics/history', { params: { limit } }),
};

// Approvals (Maker-Checker) API
export const approvalsApi = {
  getConfig: () => api.get('/approvals/config'),
  updateConfig: (data) => api.put('/approvals/config', data),
  request: (data) => api.post('/approvals/request', data),
  pending: () => api.get('/approvals/pending'),
  myRequests: () => api.get('/approvals/my-requests'),
  all: (params) => api.get('/approvals/all', { params }),
  approve: (id, data) => api.post(`/approvals/${id}/approve`, data),
  reject: (id, data) => api.post(`/approvals/${id}/reject`, data),
  count: () => api.get('/approvals/count'),
};

// TPA Portal API
export const tpaApi = {
  login: (data) => api.post('/tpa/login', data),
  users: () => api.get('/tpa/users'),
  createUser: (data) => api.post('/tpa/users', data),
  toggleUser: (id) => api.patch(`/tpa/users/${id}/toggle`),
  claims: (params) => api.get('/tpa/claims', { params }),
  claimDetail: (id) => api.get(`/tpa/claims/${id}`),
  raiseQuery: (claimId, data) => api.post(`/tpa/claims/${claimId}/queries`, data),
  respondPreauth: (claimId, data) => api.post(`/tpa/claims/${claimId}/preauth-respond`, data),
  dashboard: () => api.get('/tpa/dashboard'),
};

// Platform Users API
export const platformUsersApi = {
  list: () => api.get('/admin/platform-users'),
  create: (data) => api.post('/admin/platform-users', data),
  update: (id, data) => api.put(`/admin/platform-users/${id}`, data),
  delete: (id) => api.delete(`/admin/platform-users/${id}`),
};

// Users API
export const usersApi = {
  list: () => api.get('/users'),
  create: (data) => api.post('/users', data),
};

// Roles & Permissions API
export const rolesApi = {
  permissions: () => api.get('/roles/permissions'),
  myPermissions: () => api.get('/roles/my-permissions'),
};

// Simple tenants list for dropdowns
export const tenantsSimpleApi = {
  list: () => api.get('/tenants/list-simple'),
};

// Subscription Plans API
export const plansApi = {
  list: () => api.get('/plans'),
  create: (data) => api.post('/plans', data),
  update: (id, data) => api.put(`/plans/${id}`, data),
  delete: (id) => api.delete(`/plans/${id}`),
  summary: () => api.get('/plans/summary'),
};

// Subscription Management API (Hospital Admin)
export const subscriptionApi = {
  myPlan: () => api.get('/subscription/my-plan'),
  availablePlans: () => api.get('/subscription/available-plans'),
  createOrder: (data) => api.post('/subscription/create-order', data),
  verifyRazorpay: (data) => api.post('/subscription/verify-razorpay', data),
};

// Hospital User Management API (Hospital Admin)
export const hospitalUsersApi = {
  list: () => api.get('/hospital/users'),
  create: (data) => api.post('/hospital/users', data),
  update: (id, data) => api.put(`/hospital/users/${id}`, data),
  toggle: (id) => api.patch(`/hospital/users/${id}/toggle`),
  delete: (id) => api.delete(`/hospital/users/${id}`),
  resetPassword: (id, data) => api.post(`/hospital/users/${id}/reset-password`, data),
};

// Hospital Settings API (Hospital Admin)
export const hospitalSettingsApi = {
  get: () => api.get('/hospital/settings'),
  updateSmtp: (data) => api.put('/hospital/settings/smtp', data),
  updateCommunication: (data) => api.put('/hospital/settings/communication', data),
  testSmtp: (email) => api.post(`/hospital/settings/test-smtp?email=${encodeURIComponent(email)}`),
  getCommunications: (params) => api.get('/hospital/communications', { params }),
  getInfo: () => api.get('/hospital/info'),
  updateInfo: (data) => api.put('/hospital/info', data),
  uploadLogo: (formData) => api.post('/hospital/logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

// Payments API
export const paymentsApi = {
  create: (data) => api.post('/payments', data),
  list: (params) => api.get('/payments', { params }),
  getForClaim: (claimId) => api.get(`/claims/${claimId}/payments`),
  update: (id, data) => api.put(`/payments/${id}`, data),
  delete: (id) => api.delete(`/payments/${id}`),
  stats: () => api.get('/dashboard/payment-stats'),
};

// Notifications API
export const notificationsApi = {
  getSettings: () => api.get('/notifications/settings'),
  updateSettings: (data) => api.put('/notifications/settings', data),
  getLogs: (limit = 50) => api.get('/notifications/logs', { params: { limit } }),
  sendTest: (email) => api.post(`/notifications/test?email=${encodeURIComponent(email)}`),
};

// Reports API
export const reportsApi = {
  claims: (params) => api.get('/reports/claims', { params }),
  claimsCsv: (params) => api.get('/reports/claims', { params: { ...params, format: 'csv' }, responseType: 'blob' }),
  payerSettlement: (params) => api.get('/reports/payer-settlement', { params }),
  payerSettlementCsv: (params) => api.get('/reports/payer-settlement', { params: { ...params, format: 'csv' }, responseType: 'blob' }),
  denialAnalysis: (params) => api.get('/reports/denial-analysis', { params }),
  denialAnalysisCsv: (params) => api.get('/reports/denial-analysis', { params: { ...params, format: 'csv' }, responseType: 'blob' }),
  arAging: (params) => api.get('/reports/ar-aging', { params }),
  arAgingCsv: () => api.get('/reports/ar-aging', { params: { format: 'csv' }, responseType: 'blob' }),
  dailySummary: (date) => api.get('/reports/daily-summary', { params: { date } }),
  branchWise: (params) => api.get('/reports/branch-wise', { params }),
  userProductivity: (params) => api.get('/reports/user-productivity', { params }),
};

// Alerts API
export const alertsApi = {
  list: () => api.get('/alerts'),
  count: () => api.get('/alerts/count'),
};

// Workflow API
export const workflowApi = {
  getConfig: () => api.get('/workflow/config'),
  updateConfig: (data) => api.put('/workflow/config', data),
  getPipeline: () => api.get('/workflow/pipeline'),
};

// Audit Logs API
export const auditApi = {
  list: (params) => api.get('/audit-logs', { params }),
};

// Platform Settings API (Super Admin)
export const platformSettingsApi = {
  get: () => api.get('/platform/settings'),
  updateSmtp: (data) => api.put('/platform/settings/smtp', data),
  updatePaymentGateways: (data) => api.put('/platform/settings/payment-gateways', data),
  testSmtp: (email) => api.post(`/platform/settings/test-smtp?email=${encodeURIComponent(email)}`),
  testRazorpay: () => api.post('/platform/settings/test-razorpay'),
  testPayu: () => api.post('/platform/settings/test-payu'),
};

export default api;
